import {Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges} from '@angular/core';
import {MatSnackBar} from "@angular/material/snack-bar";
import {isReservedDocumentKey, RateConfig, RateConfigDocument, DEFAULT_PREVIEW_PAGE_COUNTS} from "../../../model/rate/RateConfig";
import {RateConfigValidator, ValidationResult} from "../../../model/rate/RateConfigValidator";
import {
  PACKING_PROPERTIES,
  RateConfigDocumentProperty,
  RateConfigEditorMode,
  RateConfigService,
  RateConfigSubjectKind
} from "../../service-data/rate-config.service";
import {ServiceProviderService} from "../../service-data/service-provider.service";
import {OrganizationService} from "../../service-data/organization.service";
import {FacilityService} from "../../service-data/facility.service";
import {Facility} from "../../../model/Facility";
import {LanguagePipe} from "../../pipe/language.pipe";

/**
 * Outer shell for the rate-configuration editor.
 *
 * Inputs identify which document on which subject is being edited; the
 * component takes care of loading, validating, importing/exporting, and
 * saving via {@link RateConfigService}.
 *
 *  - `subjectKind` + `subjectId` — which entity owns the document.
 *  - `documentProperty` — which column on that entity to load/save.
 *  - `editorMode` — `'AUTHORITATIVE'` (replace) or `'OVERRIDE'` (layer atop a base).
 *  - `readOnly` — when true, all controls are disabled. Forced true when the
 *    service reports it (e.g. SP role on charged config).
 *
 * The component does NOT route — it is meant to be embedded inside the
 * Network Settings, Facility Settings, ServiceProvider, or Organization host
 * page (matches the project's "host page embeds feature" convention).
 */
@Component({
  selector: 'app-rate-config-editor',
  templateUrl: './rate-config-editor.component.html',
  styleUrls: ['./rate-config-editor.component.css']
})
export class RateConfigEditorComponent implements OnInit, OnChanges {
  @Input() subjectKind: RateConfigSubjectKind;
  @Input() subjectId: number;
  @Input() documentProperty: RateConfigDocumentProperty;
  @Input() editorMode: RateConfigEditorMode = 'AUTHORITATIVE';
  /** When true, controls are disabled regardless of service-provided readOnly. */
  @Input() readOnly: boolean = false;

  @Output() saved = new EventEmitter<RateConfigDocument>();
  @Output() dirtyChange = new EventEmitter<boolean>();

  doc: RateConfigDocument = {};
  /** Last-saved snapshot for the Diff button and dirty tracking. */
  serverSnapshot: RateConfigDocument = {};
  /** Parent SP `rateConfig` used in OVERRIDE mode for the side-by-side preview. */
  baseDoc: RateConfigDocument = {};
  /**
   * Companion cost document for the Rates Preview's margin column. Populated
   * only when editing `ServiceProvider.rateConfig` — in that case this holds
   * the matching `serviceProviderChargedRateConfig` (what the network charges
   * the SP), so the preview can show the margin between the rate the SP
   * will charge customers and the cost the SP pays. Empty for every other
   * subjectKind / documentProperty combination.
   */
  companionDoc: RateConfigDocument = {};
  /** Display label for {@link companionDoc} (used in tooltips / legend). */
  companionLabel: string = '';
  /**
   * Facilities available for side-by-side comparison in the Rates Preview.
   * Populated only when editing `ServiceProvider.rateConfig` (the
   * comparison only makes sense in that context — the SP charges its
   * organizations, the facilities charge the SP).
   */
  availableFacilities: Facility[] = [];
  /** User's pick of facilities to surface as `peerDocs` in the preview. */
  selectedFacilityIds: number[] = [];
  /**
   * `peerDocs` derived from {@link selectedFacilityIds}. Re-computed
   * whenever the selection changes. Each entry's `label` is the
   * facility's friendly name (falling back to `name`) so the preview's
   * per-peer line is human-readable; each `doc` is the facility's
   * parsed `rateConfig` document.
   */
  peerDocs: { label: string; doc: RateConfigDocument }[] = [];

  /**
   * Whether the companion-cost document (e.g. the SP's
   * `serviceProviderChargedRateConfig`) should be rendered as a
   * synthetic peer line in the preview. Defaults to `true` when a
   * companion is loaded so the comparison shows out of the box; the
   * preview's in-line checkbox flips this without losing the loaded
   * data, so the user can declutter without re-loading.
   */
  showCompanionAsPeer: boolean = true;
  currency: string = 'USD';
  /** Result from the most recent validation pass. */
  validation: ValidationResult = {valid: true, errors: [], warnings: []};
  loadState: 'INITIAL' | 'LOADING' | 'LOADED' | 'ERROR' = 'INITIAL';
  activeScope: string = '';
  saving: boolean = false;
  /** Service-provided read-only (independent of the @Input readOnly). */
  serviceReadOnly: boolean = false;
  /** Diff-vs-server inline display. */
  diffText: string = '';
  showDiff: boolean = false;

  constructor(private rateConfigService: RateConfigService,
              private serviceProviderService: ServiceProviderService,
              private organizationService: OrganizationService,
              private facilityService: FacilityService,
              private snackBar: MatSnackBar) {}

  // ── Toasts ────────────────────────────────────────────────────────────
  // Use Material snack-bars instead of the legacy stacked-banner AlertService
  // so save/import/format feedback is unobtrusive and auto-dismisses.

  private toastSuccess(message: string): void {
    this.snackBar.open(message, LanguagePipe.get('Dismiss', 'compressSpaces'),
      {duration: 4000, panelClass: ['rce-snack-success']});
  }

  private toastError(message: string): void {
    this.snackBar.open(message, LanguagePipe.get('Dismiss', 'compressSpaces'),
      {duration: 6000, panelClass: ['rce-snack-error']});
  }

  ngOnInit(): void {
    this.load();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['subjectId'] || changes['subjectKind'] || changes['documentProperty']) {
      this.load();
    }
  }

  /** True when any control should be disabled. */
  get effectiveReadOnly(): boolean {
    return this.readOnly || this.serviceReadOnly;
  }

  /** True when local doc differs from the last-loaded server snapshot. */
  get isDirty(): boolean {
    return JSON.stringify(this.doc || {}) !== JSON.stringify(this.serverSnapshot || {});
  }

  /**
   * True when we're editing the SP's authoritative `rateConfig` OR
   * `packingRateConfig` — the only hosts where the per-facility
   * side-by-side comparison makes sense (the SP charges its
   * organizations; the facilities charge the SP). Drives whether the
   * editor populates {@link availableFacilities} (and therefore
   * whether the preview's facility multi-select is rendered at all).
   */
  get showFacilityPeerPicker(): boolean {
    return this.subjectKind === 'serviceProvider'
      && (this.documentProperty === 'rateConfig' || this.documentProperty === 'packingRateConfig');
  }

  /**
   * True when the document being edited is a PACKING rate-config variant
   * (`packingRateConfig`, `serviceProviderChargedPackingRateConfig`, or
   * `packingRateConfigOverride`). Drives the visibility of packing-only
   * affordances downstream:
   *  - the "Per package" {@link FactorKind} section (only meaningful for
   *    packing — production scopes never see `packageTypes[]` so the
   *    factor evaluates to 0);
   *  - the "Packing preview" controls in the Rates Preview (the
   *    order-level `evaluateOrder` entry point is the packing layer's
   *    primary surface; production rates only ever evaluate per-item);
   *  - the `Per Package` UI hint copy on factor-kind labels.
   *
   * Production rate-configs (SP `rateConfig` / facility `rateConfig` /
   * org `rateConfigOverride` / SP `serviceProviderChargedRateConfig`)
   * return `false` here so those packing-specific surfaces disappear.
   * See `docs/contracts/shared/PackingRateConfig.md`.
   */
  get isPackingConfig(): boolean {
    return PACKING_PROPERTIES.indexOf(this.documentProperty) >= 0;
  }

  /**
   * Lightweight `{id, label}` projection of {@link availableFacilities}
   * for the preview-matrix's facility multi-select. The preview deals
   * in opaque labels rather than full Facility objects so it stays
   * decoupled from the model.
   *
   * MEMOIZED as a plain field (not a getter) — Angular change detection
   * evaluates template bindings repeatedly, and returning a freshly-
   * built array on every access made the preview-matrix's `ngOnChanges`
   * fire on every CD pass, looping through engine evaluation forever
   * and freezing the tab. Refreshed via {@link refreshAvailableFacilityChoices}
   * whenever {@link availableFacilities} actually changes.
   */
  availableFacilityChoices: { id: number; label: string }[] = [];

  private refreshAvailableFacilityChoices(): void {
    this.availableFacilityChoices = this.availableFacilities.map(f => ({
      id: f.ID,
      label: (f.friendlyName || f.name || ('#' + f.ID)) + ' (' + f.ID + ')'
    }));
  }

  // ── Loading ──────────────────────────────────────────────────────────

  load(): void {
    if (!this.subjectKind || !this.subjectId || !this.documentProperty) { return; }
    this.loadState = 'LOADING';
    this.rateConfigService.loadFor(this.subjectKind, this.subjectId, this.documentProperty).subscribe(result => {
      if (!result) { this.loadState = 'ERROR'; return; }
      this.doc = result.doc || {};
      this.serverSnapshot = JSON.parse(JSON.stringify(this.doc));
      this.currency = result.currency || 'USD';
      this.serviceReadOnly = !!result.readOnly;
      this.activeScope = this.firstScope(this.doc) || '';
      this.refreshActiveConfig();
      this.validation = RateConfigValidator.validate(this.doc);
      this.dirtyChange.emit(false);
      this.loadState = 'LOADED';
      // For OVERRIDE mode, hydrate the parent SP base document.
      if (this.editorMode === 'OVERRIDE' && this.subjectKind === 'organization') {
        this.organizationService.getOrganization('' + this.subjectId).subscribe(org => {
          if (org && org.serviceProvider_ID) {
            this.serviceProviderService.getServiceProvider('' + org.serviceProvider_ID).subscribe(sp => {
              if (sp) { this.baseDoc = sp.getRateConfig() || {}; }
            });
          }
        });
      }
      // When editing the SP's `rateConfig` (what the SP charges customers),
      // also fetch the matching `serviceProviderChargedRateConfig` (what
      // the network charges the SP) so the Rates Preview can render the
      // margin between the two. Only meaningful for the
      // (serviceProvider, rateConfig) combination — every other host
      // shape (facility rateConfig, SP chargedRateConfig viewed directly,
      // organization rateConfigOverride) leaves the companion empty.
      this.companionDoc = {};
      this.companionLabel = '';
      this.availableFacilities = [];
      this.selectedFacilityIds = [];
      this.peerDocs = [];
      this.showCompanionAsPeer = true;
      this.refreshAvailableFacilityChoices();
      // Companion-cost wiring. Two parallel cases:
      //  - production: editing SP `rateConfig` → companion is the SP's
      //    `serviceProviderChargedRateConfig` (network cost), facility
      //    peers source from `Facility.rateConfig`.
      //  - packing:    editing SP `packingRateConfig` → companion is the
      //    SP's `serviceProviderChargedPackingRateConfig` (network packing
      //    cost), facility peers source from `Facility.packingRateConfig`.
      // Every other host shape (facility direct, SP charged-by-network
      // viewed directly, organization overrides) leaves the companion
      // empty.
      const editingSpProductionRate = this.subjectKind === 'serviceProvider' && this.documentProperty === 'rateConfig';
      const editingSpPackingRate    = this.subjectKind === 'serviceProvider' && this.documentProperty === 'packingRateConfig';
      if (editingSpProductionRate || editingSpPackingRate) {
        this.serviceProviderService.getServiceProvider('' + this.subjectId).subscribe(sp => {
          if (sp) {
            this.companionDoc = editingSpPackingRate
              ? (sp.getServiceProviderChargedPackingRateConfig() || {})
              : (sp.getServiceProviderChargedRateConfig() || {});
            this.companionLabel = LanguagePipe.get(
              editingSpPackingRate ? 'network packing cost' : 'network cost', 'compressSpaces');
          }
        });
        // Populate the facility picker for side-by-side comparison.
        // Facilities are loaded via the live data service; sort by
        // friendly name so the multi-select is alphabetized. If the
        // service hasn't loaded yet (e.g. fresh page load straight
        // into Network Settings), trigger a load so the picker fills
        // in once data arrives.
        const refreshFacilities = () => {
          // Filter on `active === true` so the peer-comparison picker
          // never surfaces a retired facility. Inactive facilities are
          // still readable on individual edit pages (for audit /
          // history) but should not appear in the network admin's
          // "compare against" multi-select. The `!== false` form
          // preserves backwards compatibility with rows that pre-date
          // the migration and therefore have no `active` column.
          // See docs/contracts/shared/ActiveFlags.md.
          this.availableFacilities = (this.facilityService.objects || []).slice()
            .filter(f => f && f.active !== false)
            .sort((a, b) => (a.friendlyName || a.name || '').localeCompare(b.friendlyName || b.name || ''));
          this.refreshAvailableFacilityChoices();
          // Re-resolve any already-selected peers against the active
          // production/packing axis so the preview's per-peer line
          // reflects the right facility document.
          this.setSelectedFacilities(this.selectedFacilityIds);
        };
        if (this.facilityService.objects && this.facilityService.objects.length > 0) {
          refreshFacilities();
        } else {
          this.facilityService.getItems().subscribe(() => refreshFacilities());
        }
      }
    });
  }

  /**
   * Recompute {@link peerDocs} from {@link selectedFacilityIds}. Called
   * whenever the user adjusts the multi-select. Facilities whose
   * `rateConfig` parses to an empty document are still included — the
   * preview will simply show `0` for every cell, which is the right
   * thing to surface (it tells the user the facility hasn't authored
   * any rates yet).
   */
  setSelectedFacilities(ids: number[]): void {
    this.selectedFacilityIds = (ids || []).slice();
    const byId = new Map<number, Facility>();
    for (const f of this.availableFacilities) { byId.set(f.ID, f); }
    // Pick the parallel document on the facility — production rates
    // peer against `Facility.rateConfig`, packing rates peer against
    // `Facility.packingRateConfig`.
    const usePacking = this.documentProperty === 'packingRateConfig';
    this.peerDocs = this.selectedFacilityIds
      .map(id => byId.get(id))
      .filter((f): f is Facility => !!f)
      .map(f => ({
        label: f.friendlyName || f.name || ('#' + f.ID),
        doc: (usePacking ? f.getPackingRateConfig() : f.getRateConfig()) || {}
      }));
  }

  /** Persist the user's choice to show or hide the companion-as-peer line. */
  setShowCompanionAsPeer(show: boolean): void {
    this.showCompanionAsPeer = !!show;
  }

  /** First non-reserved key of `doc` (skips base-level fields like
   *  `_previewPageCounts`, `_previewBookQuantities`, `facilities`). */
  private firstScope(doc: RateConfigDocument): string {
    for (const k of Object.keys(doc || {})) {
      if (!isReservedDocumentKey(k)) { return k; }
    }
    return '';
  }

  // ── Document mutation ────────────────────────────────────────────────

  setDoc(next: RateConfigDocument): void {
    if (this.effectiveReadOnly) { return; }
    this.doc = next;
    this.refreshActiveConfig();
    this.validation = RateConfigValidator.validate(this.doc);
    this.dirtyChange.emit(this.isDirty);
  }

  setActiveScope(scope: string): void {
    this.activeScope = scope;
    this.refreshActiveConfig();
  }

  setScopeConfig(updated: Partial<RateConfig>): void {
    if (!this.activeScope) { return; }
    const next: RateConfigDocument = {...this.doc, [this.activeScope]: updated};
    this.setDoc(next);
  }


  setPreviewPageCounts(raw: string): void {
    const parsed = (raw || '')
      .split(/[\s,]+/)
      .map(s => s.trim())
      .filter(s => s.length > 0)
      .map(s => Number(s))
      .filter(n => isFinite(n) && n > 0 && Math.floor(n) === n);
    const next: RateConfigDocument = {...this.doc};
    if (parsed.length === 0) {
      delete next._previewPageCounts;
    } else {
      next._previewPageCounts = parsed;
    }
    this.setDoc(next);
  }

  /**
   * Variant of {@link setPreviewPageCounts} that takes a pre-parsed array
   * (or undefined to reset to defaults). Wired up to the rates-preview
   * panel's `(pageCountsChange)` so the page-counts UI can live next to
   * the rates table it affects.
   */
  setPreviewPageCountsArray(parsed: number[] | undefined): void {
    const next: RateConfigDocument = {...this.doc};
    if (!parsed || parsed.length === 0) {
      delete next._previewPageCounts;
    } else {
      next._previewPageCounts = parsed;
    }
    this.setDoc(next);
  }

  /** Convert the page-count array to a comma-separated string for the input. */
  get previewPageCountsString(): string {
    const arr = this.doc && this.doc._previewPageCounts;
    return Array.isArray(arr) ? arr.join(', ') : DEFAULT_PREVIEW_PAGE_COUNTS.join(', ');
  }

  /**
   * Stable reference to the currently-active scope's config. Recomputed only
   * when `doc` or `activeScope` change (via {@link refreshActiveConfig}) so
   * that `[config]="activeConfig"` doesn't pass a fresh object reference on
   * every change-detection cycle (which would cascade into infinite CD via
   * the ScopePanel's child inputs).
   */
  activeConfig: Partial<RateConfig> = {};
  /** Sentinel empty-config object — singleton so referential equality holds. */
  private static readonly EMPTY_CONFIG: Partial<RateConfig> = Object.freeze({}) as Partial<RateConfig>;

  private refreshActiveConfig(): void {
    if (this.activeScope && this.doc && this.doc[this.activeScope]) {
      this.activeConfig = this.doc[this.activeScope] as Partial<RateConfig>;
    } else {
      this.activeConfig = RateConfigEditorComponent.EMPTY_CONFIG;
    }
  }

  // ── Toolbar actions ──────────────────────────────────────────────────

  validateNow(): void {
    this.validation = RateConfigValidator.validate(this.doc);
    if (this.validation.valid) {
      this.toastSuccess('Rate configuration is valid.');
    } else {
      this.toastError(`Validation failed with ${this.validation.errors.length} error(s).`);
    }
  }

  format(): void {
    // Re-serialize via JSON to drop undefineds and compact whitespace.
    try {
      const text = JSON.stringify(this.doc);
      this.setDoc(JSON.parse(text));
      this.toastSuccess('Document formatted.');
    } catch (e) {
      this.toastError('Format failed: invalid document.');
    }
  }

  importJson(text: string): void {
    if (this.effectiveReadOnly) { return; }
    let parsed: any;
    try { parsed = JSON.parse(text); }
    catch (e) { this.toastError('Import failed: not valid JSON.'); return; }
    const validation = RateConfigValidator.validate(parsed);
    if (!validation.valid) {
      this.toastError(`Import refused: ${validation.errors.length} validation error(s).`);
      this.validation = validation;
      return;
    }
    this.setDoc(parsed);
    this.activeScope = this.firstScope(parsed) || '';
    this.refreshActiveConfig();
    this.toastSuccess('Document imported.');
  }

  exportJson(): string {
    return JSON.stringify(this.doc, null, 2);
  }

  copyExport(): void {
    const text = this.exportJson();
    if (typeof navigator !== 'undefined' && navigator.clipboard) {
      navigator.clipboard.writeText(text).then(
        () => this.toastSuccess('Copied to clipboard.'),
        () => this.toastError('Could not copy to clipboard.')
      );
    }
  }

  /** Toggle a simple JSON-text diff between local doc and last-loaded server snapshot. */
  toggleDiff(): void {
    this.showDiff = !this.showDiff;
    if (this.showDiff) {
      const a = JSON.stringify(this.serverSnapshot, null, 2);
      const b = JSON.stringify(this.doc, null, 2);
      if (a === b) {
        this.diffText = '— No changes —';
      } else {
        // Side-by-side for now; a real JSON-diff lib can replace this later.
        this.diffText = `# server\n${a}\n\n# local\n${b}`;
      }
    }
  }

  save(): void {
    if (this.saving) { return; }
    // Surface UX-confusing short-circuits via toast (and console) instead of
    // returning silently. Helps diagnose "I clicked Save and nothing happened"
    // reports where the disable-state of the button doesn't match the user's
    // mental model (e.g. effectiveReadOnly true while banner missed).
    if (this.effectiveReadOnly) {
      this.toastError('Cannot save: this document is read-only for your role.');
      console.warn('RateConfigEditor.save: refused — effectiveReadOnly=true', {
        subjectKind: this.subjectKind, subjectId: this.subjectId,
        documentProperty: this.documentProperty,
        readOnlyInput: this.readOnly, serviceReadOnly: this.serviceReadOnly
      });
      return;
    }
    if (!this.isDirty) {
      this.toastError('Nothing to save — no unsaved changes.');
      return;
    }
    this.validation = RateConfigValidator.validate(this.doc);
    if (!this.validation.valid) {
      this.toastError(`Cannot save: fix ${this.validation.errors.length} validation error(s) first.`);
      return;
    }
    // Snapshot the active facility catalog into `doc.facilities` so
    // the server's `Orders.rate()` flow can resolve which facilities
    // this document applies to. Contract:
    // `docs/contracts/inbox/_processed/2026-07-08-rateconfig-facilities-field.md`.
    // Called on every save (not just SP `rateConfig` — the field is a
    // no-op for the other subject/property combinations but is cheap
    // to include, keeps documents uniform, and future-proofs any
    // subsequent subject-kind promotion of the field). Populated from
    // {@link FacilityService.objects} rather than {@link
    // availableFacilities} so the snapshot works even outside the
    // SP-editing context where {@link availableFacilities} would be
    // empty.
    this.snapshotFacilitiesIntoDoc();
    this.saving = true;
    console.log('RateConfigEditor.save: calling saveFor', {
      subjectKind: this.subjectKind, subjectId: this.subjectId,
      documentProperty: this.documentProperty, doc: this.doc
    });
    this.rateConfigService.saveFor(this.subjectKind, this.subjectId, this.documentProperty, this.doc).subscribe(ok => {
      this.saving = false;
      console.log('RateConfigEditor.save: saveFor emitted', {ok});
      if (ok) {
        this.serverSnapshot = JSON.parse(JSON.stringify(this.doc));
        // Force a top-down reference refresh of `doc` (and therefore
        // `activeConfig`) so any descendant — most importantly the
        // Rates Preview — re-runs `ngOnChanges` and rebuilds against
        // the just-saved state. Without this, child components that
        // received the SAME doc reference both before and after save
        // (because mutations during editing went through `setDoc` but
        // save itself doesn't replace the reference) can skip their
        // change-detection rebuild and continue showing stale data.
        this.doc = {...this.doc};
        this.refreshActiveConfig();
        this.dirtyChange.emit(false);
        this.toastSuccess('Saved.');
        this.saved.emit(this.doc);
      } else {
        this.toastError('Save failed.');
      }
    }, err => {
      this.saving = false;
      console.error('RateConfigEditor.save: saveFor errored', err);
      this.toastError('Save failed: ' + (err && err.message ? err.message : 'unknown error'));
    });
  }

  /**
   * Write the current active-facility ID list into `doc.facilities`.
   * The server's `Orders.rate()` (POST `/order/productionrates/item`)
   * reads this field to decide which facilities to include in the
   * per-item rate table — replacing the pre-2026-07 lookup that used
   * `Organization.printnode_ID`. Contract:
   * `docs/contracts/inbox/_processed/2026-07-08-rateconfig-facilities-field.md`.
   *
   * Reads from {@link FacilityService.objects} rather than
   * {@link availableFacilities} because {@link availableFacilities}
   * is only populated in the SP-editing context (production or
   * packing SP rateConfig); this method is called on EVERY save so
   * the field is uniformly populated regardless of subject/property.
   * The `active !== false` filter mirrors the peer-comparison
   * multi-select's existing logic (`docs/contracts/shared/ActiveFlags.md`)
   * so retired facilities never leak into a fresh save. IDs are
   * sorted ascending for byte-stable JSON so the same catalog
   * produces the same JSON bytes across saves (helps the "diff vs.
   * server" toggle and any future signature-based change detection).
   *
   * When the facility service catalog is empty (fresh page load
   * before any facility data has arrived), the method leaves the
   * field UNSET rather than writing an empty array — an empty
   * catalog is more likely a "not yet loaded" state than a
   * legitimate "no facilities" state, and writing `[]` would tell
   * the server to return an empty rate table.
   */
  private snapshotFacilitiesIntoDoc(): void {
    const catalog = this.facilityService && this.facilityService.objects
      ? this.facilityService.objects : [];
    if (!catalog || catalog.length === 0) {
      // Empty catalog is more likely "not yet loaded" than "no
      // facilities exist" — leave the field unset so the server's
      // "empty → empty table" fallback applies only when the author
      // has deliberately cleared the list.
      return;
    }
    const activeIds: number[] = catalog
      .filter(f => f && f.active !== false && typeof f.ID === 'number')
      .map(f => f.ID)
      .sort((a, b) => a - b);
    // Persist even when zero active facilities exist — that's a
    // legitimate "no facilities in scope" authoring statement (the
    // server will honor it by returning an empty table).
    this.doc.facilities = activeIds;
  }
}

